import { TableColumn } from '../types/domain';

/**
 * Returns a realistic sample value for a given column based on its data_type, key, and name.
 */
export function getSampleValueForColumn(col: TableColumn): any {
  if (col.validation_rules?.subfields && col.validation_rules.subfields.length > 0) {
    const subObj: Record<string, any> = {};
    for (const sub of col.validation_rules.subfields) {
      if (sub.sample_value) {
        subObj[sub.key] = sub.data_type === 'number' && !isNaN(Number(sub.sample_value)) ? Number(sub.sample_value) : sub.sample_value;
      } else if (sub.data_type === 'number') {
        subObj[sub.key] = 42;
      } else if (sub.data_type === 'boolean') {
        subObj[sub.key] = true;
      } else if (sub.data_type === 'email') {
        subObj[sub.key] = 'user@example.com';
      } else if (sub.data_type === 'uuid') {
        subObj[sub.key] = 'prd_4401';
      } else {
        subObj[sub.key] = `Sample ${sub.name || sub.key}`;
      }
    }
    if (col.data_type === 'array' || col.data_type === 'list') {
      return [subObj];
    }
    return subObj;
  }

  if (col.validation_rules?.sample_value) {
    const raw = col.validation_rules.sample_value;
    if (col.data_type === 'number') {
      const num = Number(raw);
      if (!isNaN(num)) return num;
    }
    if (col.data_type === 'boolean') {
      return raw.toLowerCase() === 'true' || raw === '1';
    }
    return raw;
  }

  if (col.default_value !== null && col.default_value !== undefined && col.default_value !== '') {
    if (col.data_type === 'boolean') {
      return col.default_value.toLowerCase() === 'true' || col.default_value === '1';
    }
    if (col.data_type === 'number') {
      const num = Number(col.default_value);
      if (!isNaN(num)) return num;
    }
    return col.default_value;
  }

  const keyLower = (col.key || '').toLowerCase();
  const nameLower = (col.name || '').toLowerCase();

  switch (col.data_type) {
    case 'uuid':
      return 'usr_9f82-a4c1';

    case 'object':
      return { item: 'Sample Object' };

    case 'array':
      return [{ item: 'Sample Entry' }];

    case 'email':
      return 'alice@example.com';

    case 'number':
      if (keyLower.includes('age')) return 28;
      if (keyLower.includes('price') || keyLower.includes('amount') || keyLower.includes('cost')) return 99.5;
      if (keyLower.includes('quantity') || keyLower.includes('qty') || keyLower.includes('count')) return 5;
      if (keyLower.includes('rating') || keyLower.includes('score')) return 5;
      if (keyLower.includes('year')) return 2026;
      return 42;

    case 'boolean':
      return true;

    case 'date':
      return '2026-09-09';

    case 'url':
      return 'https://example.com';

    case 'json':
      return { status: 'active', tags: ['lead', 'web'] };

    case 'status':
      return 'active';

    case 'file':
      return {
        file_id: '550e8400-e29b-41d4-a716-446655440000',
        filename: 'document.pdf',
        size_bytes: 1048576,
        content_type: 'application/pdf',
      };

    case 'text':
    default:
      if (keyLower.includes('name') || nameLower.includes('name')) return 'Alice Explorer';
      if (keyLower.includes('company') || keyLower.includes('org')) return 'Acme Corp';
      if (keyLower.includes('phone') || keyLower.includes('tel')) return '+1-555-0199';
      if (keyLower.includes('city')) return 'San Francisco';
      if (keyLower.includes('country')) return 'United States';
      if (keyLower.includes('address')) return '123 Market Street';
      if (keyLower.includes('zip') || keyLower.includes('postal')) return '94105';
      if (keyLower.includes('role') || keyLower.includes('title') || keyLower.includes('position')) return 'Software Engineer';
      if (
        keyLower.includes('message') ||
        keyLower.includes('comment') ||
        keyLower.includes('note') ||
        keyLower.includes('desc')
      ) {
        return 'Hello from Ericius! Looking forward to hearing from you.';
      }
      return `Sample ${col.name || col.key}`;
  }
}

/**
 * Structured variable for snippet code generation.
 */
export interface SnippetVariable {
  key: string;
  name: string;
  dataType: string;
  required: boolean;
  sampleValue: any;
  placeholder?: string;
}

/**
 * Builds a unified array of snippet variables from table columns.
 * When columns are empty or missing, returns a sensible default sample array.
 */
export function generateSnippetVariables(
  columns?: TableColumn[],
  honeypotField: string = '_gotcha'
): SnippetVariable[] {
  if (!columns || columns.length === 0) {
    return [
      {
        key: 'name',
        name: 'Full Name',
        dataType: 'text',
        required: true,
        sampleValue: 'Jane Smith',
        placeholder: 'Alice Explorer',
      },
      {
        key: 'email',
        name: 'Email Address',
        dataType: 'email',
        required: true,
        sampleValue: 'jane@example.com',
        placeholder: 'alice@example.com',
      },
      {
        key: 'company',
        name: 'Company',
        dataType: 'text',
        required: false,
        sampleValue: 'Acme Corp',
        placeholder: 'Acme Corp',
      },
      {
        key: 'message',
        name: 'Message',
        dataType: 'text',
        required: false,
        sampleValue: 'Testing webhook ingestion',
        placeholder: 'How can we help?',
      },
    ];
  }

  const userCols = columns.filter((c) => !c.key.startsWith('_') && c.key !== honeypotField);
  if (userCols.length === 0) {
    return [
      {
        key: 'name',
        name: 'Full Name',
        dataType: 'text',
        required: true,
        sampleValue: 'Jane Smith',
        placeholder: 'Alice Explorer',
      },
      {
        key: 'email',
        name: 'Email Address',
        dataType: 'email',
        required: true,
        sampleValue: 'jane@example.com',
        placeholder: 'alice@example.com',
      },
      {
        key: 'company',
        name: 'Company',
        dataType: 'text',
        required: false,
        sampleValue: 'Acme Corp',
        placeholder: 'Acme Corp',
      },
      {
        key: 'message',
        name: 'Message',
        dataType: 'text',
        required: false,
        sampleValue: 'Testing webhook ingestion',
        placeholder: 'How can we help?',
      },
    ];
  }

  return userCols.map((col) => {
    const sampleVal = getSampleValueForColumn(col);
    return {
      key: col.key,
      name: col.name || col.key,
      dataType: col.data_type || 'text',
      required: Boolean(col.is_required),
      sampleValue: sampleVal,
      placeholder: typeof sampleVal === 'string' ? sampleVal : (col.name || col.key),
    };
  });
}

/**
 * Builds a sample JSON object populated with keys and sample values from the schema columns.
 */
export function buildSampleDataObject(columns?: TableColumn[]): Record<string, any> {
  if (!columns || columns.length === 0) {
    return {
      name: 'Jane Smith',
      email: 'jane@example.com',
      company: 'Acme Corp',
      message: 'Testing webhook ingestion',
    };
  }

  const sample: Record<string, any> = {};
  for (const col of columns) {
    if (!col.key) continue;
    sample[col.key] = getSampleValueForColumn(col);
  }
  return sample;
}

/**
 * Generates an HTML form snippet from an array of snippet variables.
 */
export function generateHtmlFormCode(
  formUrl: string,
  redirectUrl: string,
  honeypot: string,
  variables: SnippetVariable[],
  isDefaultSample: boolean = false
): string {
  const honeypotField = honeypot || '_gotcha';

  if (isDefaultSample || !variables || variables.length === 0) {
    return `<!-- Ericius HTML Form Ingestion -->
<form action="${formUrl}" method="POST">
  <!-- Optional redirect URL after submission -->
  <input type="hidden" name="_redirect" value="${redirectUrl}">

  <!-- Honeypot spam trap: keep hidden; bots fill it and are silently quarantined -->
  <input type="text" name="${honeypotField}" style="display:none !important" tabindex="-1" autocomplete="off">

  <!-- Dynamic Fields: discovered & added automatically to your data grid -->
  <div>
    <label for="name">Full Name</label>
    <input type="text" id="name" name="name" required placeholder="Alice Explorer">
  </div>

  <div>
    <label for="email">Email Address</label>
    <input type="email" id="email" name="email" required placeholder="alice@example.com">
  </div>

  <div>
    <label for="message">Message</label>
    <textarea id="message" name="message" placeholder="How can we help?"></textarea>
  </div>

  <button type="submit">Submit Form</button>
</form>`;
  }

  const userVars = variables.filter((v) => !v.key.startsWith('_') && v.key !== honeypotField);
  const hasFiles = userVars.some((col) => col.dataType === 'file');
  const enctypeAttr = hasFiles ? ' enctype="multipart/form-data"' : '';

  const fieldsHtml = userVars
    .map((col) => {
      const idAttr = col.key;
      const nameAttr = col.key;
      const labelText = col.name || col.key;
      const isReq = col.required ? ' required' : '';
      const sampleVal = col.sampleValue;
      const placeholder = typeof sampleVal === 'string' ? ` placeholder="${sampleVal}"` : '';

      switch (col.dataType) {
        case 'file':
          return `  <div>\n    <label for="${idAttr}">${labelText}</label>\n    <input type="file" id="${idAttr}" name="${nameAttr}"${isReq}>\n  </div>`;

        case 'email':
          return `  <div>\n    <label for="${idAttr}">${labelText}</label>\n    <input type="email" id="${idAttr}" name="${nameAttr}" placeholder="user@example.com"${isReq}>\n  </div>`;

        case 'number':
          return `  <div>\n    <label for="${idAttr}">${labelText}</label>\n    <input type="number" id="${idAttr}" name="${nameAttr}" placeholder="0"${isReq}>\n  </div>`;

        case 'boolean':
          return `  <div>\n    <label for="${idAttr}">\n      <input type="checkbox" id="${idAttr}" name="${nameAttr}" value="true"${isReq}> ${labelText}\n    </label>\n  </div>`;

        case 'date':
          return `  <div>\n    <label for="${idAttr}">${labelText}</label>\n    <input type="date" id="${idAttr}" name="${nameAttr}"${isReq}>\n  </div>`;

        case 'url':
          return `  <div>\n    <label for="${idAttr}">${labelText}</label>\n    <input type="url" id="${idAttr}" name="${nameAttr}" placeholder="https://example.com"${isReq}>\n  </div>`;

        case 'json':
          return `  <div>\n    <label for="${idAttr}">${labelText}</label>\n    <textarea id="${idAttr}" name="${nameAttr}" placeholder='{"key": "value"}'${isReq}></textarea>\n  </div>`;

        case 'text':
        default: {
          const keyLower = col.key.toLowerCase();
          if (
            keyLower.includes('message') ||
            keyLower.includes('description') ||
            keyLower.includes('comment') ||
            keyLower.includes('bio') ||
            keyLower.includes('notes')
          ) {
            return `  <div>\n    <label for="${idAttr}">${labelText}</label>\n    <textarea id="${idAttr}" name="${nameAttr}" placeholder="Enter ${labelText.toLowerCase()}..."${isReq}></textarea>\n  </div>`;
          }
          return `  <div>\n    <label for="${idAttr}">${labelText}</label>\n    <input type="text" id="${idAttr}" name="${nameAttr}"${placeholder}${isReq}>\n  </div>`;
        }
      }
    })
    .join('\n\n');

  return `<!-- Ericius HTML Form Ingestion -->
<form action="${formUrl}" method="POST"${enctypeAttr}>
  <!-- Optional redirect URL after submission -->
  <input type="hidden" name="_redirect" value="${redirectUrl}">

  <!-- Honeypot spam trap: keep hidden; bots fill it and are silently quarantined -->
  <input type="text" name="${honeypotField}" style="display:none !important" tabindex="-1" autocomplete="off">

  <!-- Schema-Defined Fields -->
${fieldsHtml}

  <button type="submit">Submit Form</button>
</form>`;
}

/**
 * Generates an HTML form snippet with input elements corresponding to the schema columns.
 */
export function generateHtmlFormSnippet(
  formUrl: string,
  redirectUrl: string,
  honeypot: string,
  columns?: TableColumn[]
): string {
  const isDefault = !columns || columns.length === 0;
  const variables = generateSnippetVariables(columns, honeypot);
  return generateHtmlFormCode(formUrl, redirectUrl, honeypot, variables, isDefault);
}

/**
 * Generates a cURL snippet from an array of snippet variables.
 */
export function generateCurlCode(
  webhookUrl: string,
  ingestToken: string,
  variables: SnippetVariable[]
): string {
  const sampleObj: Record<string, any> = {};
  for (const v of variables) {
    if (!v.key) continue;
    sampleObj[v.key] = v.sampleValue;
  }

  const formattedJson = JSON.stringify(sampleObj, null, 2)
    .split('\n')
    .map((line, idx) => (idx === 0 ? line : `  ${line}`))
    .join('\n');

  return `# REST API Webhook Ingestion
curl -X POST "${webhookUrl}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${ingestToken}" \\
  -d '${formattedJson}'`;
}

/**
 * Generates a cURL snippet with JSON payload formatted from the schema columns.
 */
export function generateCurlSnippet(
  webhookUrl: string,
  ingestToken: string,
  columns?: TableColumn[]
): string {
  const variables = generateSnippetVariables(columns);
  return generateCurlCode(webhookUrl, ingestToken, variables);
}

/**
 * Generates a JavaScript fetch snippet from an array of snippet variables.
 */
export function generateFetchCode(
  webhookUrl: string,
  ingestToken: string,
  variables: SnippetVariable[]
): string {
  const sampleObj: Record<string, any> = {};
  for (const v of variables) {
    if (!v.key) continue;
    sampleObj[v.key] = v.sampleValue;
  }

  const formattedJson = JSON.stringify(sampleObj, null, 2)
    .split('\n')
    .map((line, idx) => (idx === 0 ? line : `    ${line}`))
    .join('\n');

  return `// REST API Webhook Ingestion with fetch
const response = await fetch("${webhookUrl}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "Authorization": "Bearer ${ingestToken}"
  },
  body: JSON.stringify(${formattedJson})
});

const result = await response.json();
console.log(result);`;
}

/**
 * Generates a JavaScript fetch snippet with JSON payload formatted from the schema columns.
 */
export function generateFetchSnippet(
  webhookUrl: string,
  ingestToken: string,
  columns?: TableColumn[]
): string {
  const variables = generateSnippetVariables(columns);
  return generateFetchCode(webhookUrl, ingestToken, variables);
}

/**
 * Generates default email body template based on schema columns.
 */
export function generateDefaultEmailBody(tableName: string, columns?: TableColumn[]): string {
  if (!columns || columns.length === 0) {
    return `<!DOCTYPE html>
<html>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; color: #111;">
  <h2 style="color: #111827; font-size: 18px; font-weight: 700; margin-bottom: 12px;">New Submission Captured</h2>
  <p>A new verified form payload was received on Ericius:</p>
  
  {{ form._all_fields }}

  <hr style="border: none; border-top: 1px solid #eaeaea; margin: 24px 0;" />
  <p style="font-size: 12px; color: #6b7280;">
    Submission Ingested At: {{ meta.created_at }} | Origin IP: {{ meta.ip }}
  </p>
</body>
</html>`;
  }

  const userCols = columns.filter((c) => !c.key.startsWith('_'));

  const tableRows = userCols
    .map(
      (col) =>
        `    <tr><td style="padding:8px 12px; border:1px solid #e5e7eb; font-weight:500;">${
          col.name || col.key
        }</td><td style="padding:8px 12px; border:1px solid #e5e7eb; font-family:monospace;">{{ form.${
          col.key
        } }}</td></tr>`
    )
    .join('\n');

  return `<!DOCTYPE html>
<html>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; color: #111;">
  <h2 style="color: #111827; font-size: 18px; font-weight: 700; margin-bottom: 12px;">New Submission Captured</h2>
  <p>A new verified submission was received for <strong>${tableName || 'Datastore'}</strong>:</p>
  
  <table style="width:100%; max-width:600px; border-collapse:collapse; font-size:13px; margin: 16px 0;">
    <thead>
      <tr style="background:#f3f4f6; text-align:left;">
        <th style="padding:8px 12px; border:1px solid #e5e7eb; font-weight:600;">Field</th>
        <th style="padding:8px 12px; border:1px solid #e5e7eb; font-weight:600;">Value</th>
      </tr>
    </thead>
    <tbody>
${tableRows}
    </tbody>
  </table>

  <hr style="border: none; border-top: 1px solid #eaeaea; margin: 24px 0;" />
  <p style="font-size: 12px; color: #6b7280;">
    Submission Ingested At: {{ meta.created_at }} | Origin IP: {{ meta.ip }}
  </p>
</body>
</html>`;
}

/**
 * Returns the default subject template based on schema columns.
 */
export function getDefaultSubjectTemplate(tableName: string, columns?: TableColumn[]): string {
  if (!columns || columns.length === 0) {
    return 'New submission: {{ form.name }}';
  }

  const nameCol = columns.find(
    (c) =>
      c.key.toLowerCase() === 'name' ||
      c.key.toLowerCase().includes('name') ||
      c.key.toLowerCase().includes('title')
  );
  if (nameCol) {
    return `New submission: {{ form.${nameCol.key} }}`;
  }

  const emailCol = columns.find((c) => c.data_type === 'email' || c.key.toLowerCase().includes('email'));
  if (emailCol) {
    return `New submission from {{ form.${emailCol.key} }}`;
  }

  return `New submission on ${tableName || 'Datastore'}`;
}

/**
 * Returns the default recipient list based on schema columns.
 */
export function getDefaultRecipients(columns?: TableColumn[]): string {
  if (!columns || columns.length === 0) {
    return 'admin@company.com, {{ form.email }}';
  }

  const emailCol = columns.find((c) => c.data_type === 'email' || c.key.toLowerCase().includes('email'));
  if (emailCol) {
    return `admin@company.com, {{ form.${emailCol.key} }}`;
  }

  return 'admin@company.com';
}
